import { c as _c } from "react/compiler-runtime";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarDays, DatabaseZap, Keyboard, Plug, Settings2, SunMoon, Users, Zap } from "lucide-react";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { ProofIconBold } from "@/components/codesmith/proof/proofIcon";
import { type SettingsSection, settingsPath, settingsRouteFromPathname } from "@/lib/settingsRoutes";
import { cn } from "@repo/blacksmith-ui/cn";
import { RoutePrefetchLink } from "../ui/route-prefetch-link";
import { Sidebar as ShadcnSidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarRail } from "@repo/blacksmith-ui/sidebar";
import { AppSidebarHeader, AppSidebarHeaderActions } from "../ui/app-header";
import { SIDEBAR_NAV_BUTTON_CLASS, SIDEBAR_NAV_ITEM_CLASS } from "@repo/blacksmith-ui/sidebar-nav";
type SectionNavItem = {
  name: string;
  section: SettingsSection;
  icon: React.ReactNode;
};

/**
 * Contextual sidebar for the settings routes. Sits to the right of the
 * app-switcher rail and links to the section pages under
 * `/[org]/settings`. Only the section page swaps on navigation; the settings
 * layout stays mounted.
 */
export default function SettingsSidebar() {
  const $ = _c(44);
  const router = useRouter();
  const {
    codesmithEnabled,
    codesmithProofSettingsEnabled,
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = typeof urlOrg === "string" ? urlOrg : activeNamedOrg?.orgName;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== codesmithEnabled || $[1] !== codesmithProofSettingsEnabled || $[2] !== currentOrg || $[3] !== router.pathname) {
    const codesmithNav = codesmithEnabled ? [{
      name: "[code]smith",
      section: "codesmith",
      icon: <CodesmithIcon className="size-[18px]" />
    }, ...(codesmithProofSettingsEnabled ? [{
      name: "Proof",
      section: "proof" as const,
      icon: <ProofIconBold className="size-[18px]" />
    }] : [])] : [];
    const nav = [{
      name: "General",
      section: "general",
      icon: <Settings2 className="size-[18px]" />
    }, {
      name: "Appearance",
      section: "appearance",
      icon: <SunMoon className="size-[18px]" />
    }, {
      name: "Features",
      section: "features",
      icon: <Zap className="size-[18px]" />
    }, {
      name: "Caching",
      section: "caching",
      icon: <DatabaseZap className="size-[18px]" />
    }, {
      name: "Integrations",
      section: "integrations",
      icon: <Plug className="size-[18px]" />
    }, {
      name: "Team",
      section: "team",
      icon: <Users className="size-[18px]" />
    }, ...codesmithNav];
    const activeSection = settingsRouteFromPathname(router.pathname)?.section ?? "general";
    T4 = ShadcnSidebar;
    t5 = "icon";
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = cn("left-[var(--app-switcher-rail-width,_48px)] transition-[left] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)]", "[&_[data-slot=sidebar-inner]]:border-[#0c0c0d14] dark:[&_[data-slot=sidebar-inner]]:border-white/12");
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <SidebarHeader className="shrink-0 bg-sidebar p-0"><AppSidebarHeader title="Settings" actions={<AppSidebarHeaderActions />} collapsible={true} /></SidebarHeader>;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    T3 = SidebarContent;
    t4 = "flex min-h-[200px] flex-col gap-0 overflow-hidden bg-sidebar py-0 group-data-[state=collapsed]:px-0 group-data-[collapsible=icon]:overflow-hidden";
    T2 = SidebarGroup;
    t3 = "border-0 min-h-0 flex-1 overflow-y-auto pt-[calc(var(--spacing-app-header-gap)-0.5rem)] group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1";
    T1 = SidebarGroupContent;
    t2 = "flex flex-1 flex-col px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0";
    T0 = SidebarMenu;
    t0 = "flex flex-col gap-1";
    t1 = nav.map(item => {
      const href = settingsPath(currentOrg, item.section);
      return <SidebarMenuItem key={item.name} className={SIDEBAR_NAV_ITEM_CLASS}><SidebarMenuButton asChild={true} isActive={activeSection === item.section} tooltip={item.name} className={SIDEBAR_NAV_BUTTON_CLASS}><RoutePrefetchLink href={href} prefetch={currentOrg ? true : null} prefetchOnIntent={!!currentOrg} className="cursor-pointer"><div className="relative">{item.icon}</div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">{item.name}</span></RoutePrefetchLink></SidebarMenuButton></SidebarMenuItem>;
    });
    $[0] = codesmithEnabled;
    $[1] = codesmithProofSettingsEnabled;
    $[2] = currentOrg;
    $[3] = router.pathname;
    $[4] = T0;
    $[5] = T1;
    $[6] = T2;
    $[7] = T3;
    $[8] = T4;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    T0 = $[4];
    T1 = $[5];
    T2 = $[6];
    T3 = $[7];
    T4 = $[8];
    t0 = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[19] !== T0 || $[20] !== t0 || $[21] !== t1) {
    t8 = <T0 className={t0}>{t1}</T0>;
    $[19] = T0;
    $[20] = t0;
    $[21] = t1;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== T1 || $[24] !== t2 || $[25] !== t8) {
    t9 = <T1 className={t2}>{t8}</T1>;
    $[23] = T1;
    $[24] = t2;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== T2 || $[28] !== t3 || $[29] !== t9) {
    t10 = <T2 className={t3}>{t9}</T2>;
    $[27] = T2;
    $[28] = t3;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== T3 || $[32] !== t10 || $[33] !== t4) {
    t11 = <T3 className={t4}>{t10}</T3>;
    $[31] = T3;
    $[32] = t10;
    $[33] = t4;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarMenuItem className={SIDEBAR_NAV_ITEM_CLASS}><SidebarMenuButton asChild={true} tooltip="Book a demo" className={SIDEBAR_NAV_BUTTON_CLASS}><a href="https://www.blacksmith.sh/contact-our-team" target="_blank" rel="noopener noreferrer"><div className="relative"><CalendarDays className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Book a demo</span></a></SidebarMenuButton></SidebarMenuItem>;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  let t14;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SidebarFooter className="shrink-0 gap-0 p-0 group-data-[state=collapsed]:border-t-0"><SidebarGroup className="border-0 py-2 group-data-[state=collapsed]:pl-3 group-data-[state=collapsed]:pr-1"><SidebarGroupContent className="px-1 py-0 group-data-[state=collapsed]:items-center group-data-[state=collapsed]:px-0"><SidebarMenu className="flex flex-col gap-1">{t12}<SidebarMenuItem className={SIDEBAR_NAV_ITEM_CLASS}><SidebarMenuButton tooltip="Keyboard shortcuts" className={SIDEBAR_NAV_BUTTON_CLASS} onClick={_temp}><div className="relative"><Keyboard className="size-[18px]" /></div><span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Keyboard shortcuts</span></SidebarMenuButton></SidebarMenuItem></SidebarMenu></SidebarGroupContent></SidebarGroup></SidebarFooter>;
    t14 = <SidebarRail />;
    $[36] = t13;
    $[37] = t14;
  } else {
    t13 = $[36];
    t14 = $[37];
  }
  let t15;
  if ($[38] !== T4 || $[39] !== t11 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7) {
    t15 = <T4 collapsible={t5} className={t6}>{t7}{t11}{t13}{t14}</T4>;
    $[38] = T4;
    $[39] = t11;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}
function _temp() {
  return document.dispatchEvent(new KeyboardEvent("keydown", {
    key: "?",
    bubbles: true,
    cancelable: true
  }));
}